import { ExceptionEvent } from "@fairfleet/geotab";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import React from "react";
import { useTranslation } from "react-i18next";
import { warningIcons } from "../rowDetail/faultDetail/FaultInstructions";
import { ModalType, useModal } from "@/components/ModalProvider";
import { WarningImportance } from "@/components/operators/WarningSortComparator";
import AddInIds from "@/constants/AddInIds";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { findPmJob, usePmJobsByDevice } from "@/hooks/maintenance/usePmJobsByDevice";
import Warning, { WarningType } from "@/models/Warning";
import RulesAddinData from "@/pages/pmrules-edit/models/RulesAddinData";

const PM_TYPES: ReadonlyArray<WarningType> = [WarningType.PM, WarningType.PM1];

export const WarningCell = ({ value, row }: GridRenderCellParams) => {
  const warnings = (value || []) as Warning[];
  const sortedWarnings = [...warnings].sort(
    (a, b) => WarningImportance[a.type] - WarningImportance[b.type],
  );

  const { data: pmAddinData = [] } = useAddinData<RulesAddinData>(AddInIds.pmrulesData);
  const { data: pmJobs } = usePmJobsByDevice(row?.device?.id ?? "");

  const { t } = useTranslation();
  const { openModal } = useModal();

  const handleTellTaleClick = (type: WarningType, event?: ExceptionEvent) => {
    openModal({ warningType: type, event }, ModalType.TELLTALE_DETAILS);
  };

  const handlePmClick = (warning: Warning, ruleAddinData?: RulesAddinData) => {
    if (!ruleAddinData) return;
    const existing = findPmJob(pmJobs, ruleAddinData.pmTitle);
    if (existing) {
      openModal(
        { workOrderJobs: [existing], workOrders: [], deviceId: row?.device?.id },
        ModalType.MAINTENANCE_DETAILS,
      );
    } else {
      openModal({ device: row.device, warning, ruleAddinData }, ModalType.CREATE_PM_ORDER);
    }
  };

  return (
    <div style={{ display: "flex", gap: "4px", height: "100%" }}>
      {sortedWarnings.map((warning, i) => {
        const rId = warning.event?.rule?.id;
        const ruleAddinData = rId ? pmAddinData.find(d => d.ruleId === rId) : undefined;
        const isPm = PM_TYPES.includes(warning.type);
        const existingPmJob =
          isPm && ruleAddinData ? findPmJob(pmJobs, ruleAddinData.pmTitle) : null;

        const tooltipTitle = isPm
          ? existingPmJob
            ? t("createPmOrder.tooltipExisting", {
                reference: existingPmJob.workOrder?.reference ?? "",
              })
            : (ruleAddinData?.pmTitle ?? t("createPmOrder.tooltipCreate"))
          : t("faultOverview." + warning.type);

        const onClick = isPm
          ? () => handlePmClick(warning, ruleAddinData)
          : [WarningType.TT_RED, WarningType.TT_YELLOW].includes(warning.type)
            ? () => handleTellTaleClick(warning.type, warning.event)
            : undefined;

        const iconStyle = isPm && existingPmJob ? { opacity: 0.5 } : undefined;

        const icon = (
          <IconButton size="small" onClick={onClick} sx={iconStyle}>
            {warningIcons[warning.type]}
          </IconButton>
        );

        return (
          <Tooltip key={i} sx={{ height: "100%", alignSelf: "center" }} title={tooltipTitle}>
            <span>{icon}</span>
          </Tooltip>
        );
      })}
    </div>
  );
};
